import React, { useState } from 'react';
import PropTypes from 'prop-types';
import TagFacesIcon from '@mui/icons-material/TagFaces';
import {
  LimitCounter,
  StatusInputContainer,
  TextFieldStyle,
} from '../UserProfile/RightPanel/Status/StatusInput.styled';
import EmojiPicker from '../emoji/EmojiPicker';
import AddReaction from '../common/Icons/AddReaction';
import emojiUtils from '@core/utils/emoji';
import { IconButton, Icons, TextField, Tooltip } from '@ublend-npm/aulaui-core';
import EmojiButton from '../common/EmojiButton';

const TextFieldWithRef = React.forwardRef((props, ref) => (
  <div ref={ref} className={TextFieldStyle}>
    <TextField {...props} />
  </div>
));

const StatusInput = ({
  statusText,
  emojiShortName,
  setStatusText,
  setEmojiShortName,
}) => {
  const [emojiPickerAnchor, setEmojiPickerAnchor] = useState(null);
  const inputRef = React.useRef();
  const limit = 100;

  const openEmojiPicker = () => setEmojiPickerAnchor(inputRef.current);
  const clear = () => {
    setStatusText('');
    setEmojiShortName('');
  };
  const hasStatus = () => statusText.length || emojiShortName.length;

  const onChangeStatus = ({ target }) => {
    setStatusText(target.value.slice(0, limit));
    if (!emojiShortName) setEmojiShortName('speech_balloon');
  };

  return (
    <StatusInputContainer>
      <TextFieldWithRef
        label="Status"
        sx={{ height: 70, '& .MuiInputBase-root': { height: 70 } }}
        placeholder="Update your status"
        data-testid="status-text"
        value={statusText}
        onChange={onChangeStatus}
        leftElement={
          <Tooltip
            title="Pick status emoji"
            aria-label="Pick status emoji"
            position="top"
          >
            {emojiShortName.length ? (
              <EmojiButton
                emoji={emojiUtils.getFromShortName(emojiShortName)}
                onClick={openEmojiPicker}
                onKeyDown={openEmojiPicker}
              />
            ) : (
              <IconButton
                size="medium"
                data-testid="pick-emoji-button"
                aria-label="Pick status emoji"
                label="pick status emoji"
                onClick={openEmojiPicker}
              >
                {' '}
                <TagFacesIcon />{' '}
              </IconButton>
            )}
          </Tooltip>
        }
        rightElement={
          hasStatus() ? (
            <Tooltip title="Clear status" position="top">
              <IconButton
                size="medium"
                label="clear status"
                onClick={clear}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    clear();
                  }
                }}
              >
                {' '}
                <Icons.Cancel />{' '}
              </IconButton>
            </Tooltip>
          ) : null
        }
        ref={inputRef}
      />
      <EmojiPicker
        title="Pick status emoji"
        id="userstatus-emoji-picker"
        anchorEl={emojiPickerAnchor}
        show={!!emojiPickerAnchor}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        onClose={() => setEmojiPickerAnchor(null)}
        onSelect={(e) => {
          setEmojiShortName(e.short_name);
          setTimeout(() => {
            const inputEl = inputRef.current?.querySelector('input');
            if (inputEl) {
              inputEl.focus();
            }
          }, 0);
        }}
      />
    </StatusInputContainer>
  );
};

StatusInput.propTypes = {
  statusText: PropTypes.string.isRequired,
  emojiShortName: PropTypes.string.isRequired,
  setStatusText: PropTypes.func.isRequired,
  setEmojiShortName: PropTypes.func.isRequired,
};

export default React.memo(StatusInput);
